/**
 * Privacy Policy Page
 * GDPR-compliant — European data protection law
 *
 * ⚠️  Before going live, update:
 *   - COMPANY_LEGAL_NAME, COMPANY_ADDRESS, COMPANY_COUNTRY
 *   - SUPERVISORY_AUTHORITY (your EU member state's DPA)
 *   - Emails: set VITE_EMAIL_PRIVACY and VITE_EMAIL_LEGAL in apps/web/.env
 */

import { usePageSEO } from "@/lib/usePageSEO"
import { Link } from "react-router-dom"
import { ArrowLeft, Shield, Trash2, Mail, Clock, CheckCircle } from "lucide-react"
import { GoogleAnalytics } from "@/components/shared/GoogleAnalytics"
import { APP_NAME, GA_ID, EMAIL_PRIVACY, EMAIL_LEGAL, LEGAL_COMPANY_NAME, LEGAL_ADDRESS_ENABLED, LEGAL_ADDRESS } from "@/lib/appConfig"

// ─── Edit these before going live ────────────────────────────────────────────
const COMPANY_LEGAL_NAME = LEGAL_COMPANY_NAME
const COMPANY_ADDRESS = LEGAL_ADDRESS
const PRIVACY_EMAIL = EMAIL_PRIVACY
const LEGAL_EMAIL = EMAIL_LEGAL
const SUPERVISORY_AUTHORITY = "your national data protection authority"
const LAST_UPDATED = "10 March 2026"
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "who-we-are", title: "Who We Are" },
  { id: "data-we-collect", title: "Data We Collect" },
  { id: "how-we-use", title: "How We Use Your Data" },
  { id: "legal-basis", title: "Legal Basis (GDPR)" },
  { id: "data-sharing", title: "Data Sharing & Processors" },
  { id: "data-retention", title: "Data Retention" },
  { id: "international-transfers", title: "International Transfers" },
  { id: "your-rights", title: "Your Rights" },
  { id: "delete-your-data", title: "Delete Your Data" },
  { id: "cookies", title: "Cookies & Tracking" },
  { id: "security", title: "Security" },
  { id: "children", title: "Children's Privacy" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "contact", title: "Contact & Complaints" },
]

function SectionHeader({ id, title }: { id: string; title: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-24 text-xl font-bold text-foreground mt-12 mb-4 pb-2 border-b border-border/60"
    >
      {title}
    </h2>
  )
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm text-foreground/80 leading-relaxed my-4">
      {children}
    </div>
  )
}

function RightCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
      <p className="font-semibold text-sm text-foreground mb-1">{title}</p>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  )
}

export default function PrivacyPolicyPage() {
  usePageSEO({
    title: `Privacy Policy | ${APP_NAME}`,
    description: `Read the ${APP_NAME} Privacy Policy. Learn how we collect, use, and protect your personal data under GDPR.`,
    path: "/privacy-policy",
  })

  return (
    <div className="min-h-screen bg-background">
      <GoogleAnalytics id={GA_ID} />
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-md border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 font-bold text-base tracking-tight shrink-0">
            <img src="/app-icon.png" className="h-7 w-7 object-contain" alt={APP_NAME} />
            <span className="hidden sm:inline">{APP_NAME.toUpperCase()}</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-muted/30 border-b border-border/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Legal Document
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Privacy Policy</h1>
          <p className="text-muted-foreground text-base leading-relaxed max-w-2xl">
            This policy explains how {APP_NAME} collects, uses, and protects your personal data in
            compliance with the General Data Protection Regulation (GDPR) and other applicable
            European privacy laws.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Last updated: <span className="font-semibold">{LAST_UPDATED}</span>
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-[200px_1fr] gap-12">
        {/* TOC Sidebar */}
        <aside className="hidden md:block">
          <div className="sticky top-20 space-y-0.5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 px-2">
              Contents
            </p>
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 px-2 py-1.5 rounded-lg transition-colors leading-snug"
              >
                {s.title}
              </a>
            ))}
            <div className="pt-4 border-t border-border/40 mt-4">
              <Link
                to="/terms-of-service"
                className="block text-sm text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg transition-colors"
              >
                → Terms of Service
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="text-sm text-foreground/80 leading-relaxed min-w-0">
          <p className="text-base leading-relaxed mb-2">
            At <strong>{APP_NAME}</strong>, we take your privacy seriously. As a platform designed
            for property owners and landlords — personal and professional alike — operating
            primarily in the United States and the European Union, we are committed to full
            compliance with the EU General Data Protection Regulation (GDPR — Regulation
            2016/679), the ePrivacy Directive, applicable US state privacy laws, and all other
            relevant national data protection laws.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            This Privacy Policy applies to all users of our web application, mobile applications,
            and any related services (collectively, the "Service"). The Service is primarily
            intended for users in the <strong>United States</strong> and the{" "}
            <strong>European Union / European Economic Area</strong>. Please read it carefully.
          </p>

          {/* ── Who We Are ── */}
          <SectionHeader id="who-we-are" title="1. Who We Are" />
          <p className="mb-3">
            <strong>{APP_NAME}</strong> is a Software-as-a-Service (SaaS) property management
            platform app managed and provided by <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-bold">SoulSpark Games</a>. The data controller responsible for your personal data is:
          </p>
          <div className="bg-muted/40 rounded-xl p-4 border border-border/40 text-sm mb-4 space-y-1">
            <p><strong>Company name:</strong> {COMPANY_LEGAL_NAME}</p>
            {LEGAL_ADDRESS_ENABLED && COMPANY_ADDRESS && (
              <p><strong>Registered address:</strong> {COMPANY_ADDRESS}</p>
            )}
            <p><strong>Privacy contact:</strong>{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">{PRIVACY_EMAIL}</a>
            </p>
            <p><strong>Legal contact:</strong>{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-primary hover:underline">{LEGAL_EMAIL}</a>
            </p>
          </div>
          <p className="text-muted-foreground text-xs leading-relaxed mb-4">
            {APP_NAME} is a product and service of <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">SoulSpark Games</a>,
            an independent technology and software company. SoulSpark Games develops digital tools
            and software products across multiple categories, of which {APP_NAME} is one. All data
            controller responsibilities for the {APP_NAME} service are held by the legal entity
            identified above.
          </p>
          <p>
            For any questions about how we handle your personal data, you may contact us at any
            time at the addresses above. We aim to respond to all privacy-related enquiries within
            the timeframe required by applicable law.
          </p>

          {/* ── Data We Collect ── */}
          <SectionHeader id="data-we-collect" title="2. Data We Collect" />
          <p className="mb-4">
            We collect only the data necessary to provide and improve our Service. The categories
            of personal data we process include:
          </p>

          <h3 className="font-semibold text-foreground mb-2">2.1 Account & Identity Data</h3>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li>Full name and email address (provided at registration)</li>
            <li>Encrypted password hash (we never store your password in plain text)</li>
            <li>Account preferences (currency, date format, timezone)</li>
            <li>Your subscription plan and billing status</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">2.2 Property & Tenant Data (Your Business Data)</h3>
          <p className="text-muted-foreground mb-2">
            As a landlord, you input data about your properties and tenants. This may include:
          </p>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li>Property addresses, descriptions, photos, and room configurations</li>
            <li>Tenant names, contact details, ID references, and lease terms</li>
            <li>Financial records: rent amounts, payment records, expense receipts</li>
            <li>Maintenance requests and associated notes or photos</li>
            <li>Documents and files uploaded to the platform</li>
          </ul>
          <InfoBox>
            <strong>Note on tenant data:</strong> You, as the landlord-user, are the data
            controller of your tenants' personal data. {APP_NAME} acts as a data processor for
            this data under Article 28 GDPR. You are responsible for ensuring you have a lawful
            basis to process your tenants' personal data and for informing your tenants about how
            their data is used.
          </InfoBox>

          <h3 className="font-semibold text-foreground mb-2">2.3 Usage & Technical Data</h3>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li>IP address and approximate geographic location (country/city level)</li>
            <li>Device type, browser type, and operating system</li>
            <li>Pages visited, features used, and session duration</li>
            <li>Error logs and performance data for debugging purposes</li>
            <li>Authentication tokens (stored locally on your device)</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">2.4 Communications Data</h3>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li>Emails sent to our support or legal addresses</li>
            <li>In-app notification preferences</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">2.5 Payment Data</h3>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200 mb-4">
            <p className="font-semibold mb-1">⏳ Paid plans are not yet available</p>
            <p className="leading-relaxed">
              {APP_NAME} does not currently offer any paid subscription plans. No payment
              system is active and <strong>no payment data of any kind is collected,
              processed, or stored</strong> through the Service at this time.
            </p>
          </div>
          <p className="text-muted-foreground mb-2">
            We plan to introduce paid plans in the future. When that happens, payment processing
            will be handled exclusively by the trusted third-party providers listed below,
            depending on the platform. We will <strong className="text-foreground">never store,
            log, or have access to your full payment card number, bank details, or raw payment
            credentials</strong> on our servers.
          </p>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li><strong className="text-foreground">Web application (planned):</strong> Payments will be processed by <strong>Stripe, Inc.</strong> (USA), who handle card tokenisation and billing directly.</li>
            <li><strong className="text-foreground">Android app (planned):</strong> In-app purchases and subscriptions will be processed by <strong>Google Play Billing</strong> (Google LLC, USA).</li>
            <li><strong className="text-foreground">iOS / iPadOS app (planned):</strong> In-app purchases and subscriptions will be processed by <strong>Apple In-App Purchase</strong> (Apple Inc., USA). Apple Pay may also be supported.</li>
            <li><strong className="text-foreground">Cross-platform subscription management (planned):</strong> <strong>RevenueCat, Inc.</strong> (USA) will serve as our subscription entitlement layer across all platforms. RevenueCat does not process payment card details.</li>
          </ul>
          <p className="text-muted-foreground mb-4">
            We will update this Privacy Policy and notify all registered users before any paid
            plans or payment processing become active. Each provider's privacy policy is linked
            in Section 5 (Data Sharing &amp; Sub-Processors) for reference.
          </p>

          {/* ── How We Use ── */}
          <SectionHeader id="how-we-use" title="3. How We Use Your Data" />
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li><strong className="text-foreground">Providing the Service:</strong> Creating and managing your account, storing and displaying your property/tenant data, sending automated reminders and notifications.</li>
            <li><strong className="text-foreground">Account security:</strong> Authentication, detecting fraud, protecting your account from unauthorised access.</li>
            <li><strong className="text-foreground">Service improvements:</strong> Analysing aggregated usage patterns to improve features and performance (anonymised where possible).</li>
            <li><strong className="text-foreground">Communications:</strong> Sending transactional emails (account confirmation, password reset, payment receipts, lease expiry alerts). We do not send marketing emails without explicit consent.</li>
            <li><strong className="text-foreground">Legal compliance:</strong> Complying with applicable laws, responding to lawful requests from authorities, resolving disputes.</li>
            <li><strong className="text-foreground">Billing & invoicing:</strong> Processing subscription payments and maintaining records required by EU tax law.</li>
          </ul>

          {/* ── Legal Basis ── */}
          <SectionHeader id="legal-basis" title="4. Legal Basis Under GDPR" />
          <p className="mb-4">
            Under Article 6 of the GDPR, we rely on the following lawful bases for processing
            your personal data:
          </p>
          <div className="space-y-3 mb-4">
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm mb-1">Art. 6(1)(b) — Performance of a Contract</p>
              <p className="text-sm text-muted-foreground">Processing your account data, property data, and tenant data to provide the Service you signed up for.</p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm mb-1">Art. 6(1)(c) — Legal Obligation</p>
              <p className="text-sm text-muted-foreground">Retaining billing and financial records to comply with EU tax and accounting regulations (typically 7 years).</p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm mb-1">Art. 6(1)(f) — Legitimate Interests</p>
              <p className="text-sm text-muted-foreground">Improving our Service, detecting and preventing fraud and abuse, maintaining platform security, and basic analytics (always balanced against your rights).</p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm mb-1">Art. 6(1)(a) — Consent</p>
              <p className="text-sm text-muted-foreground">Where we rely on consent (e.g. optional marketing communications), you may withdraw it at any time without affecting the lawfulness of prior processing.</p>
            </div>
          </div>

          {/* ── Data Sharing ── */}
          <SectionHeader id="data-sharing" title="5. Data Sharing & Sub-Processors" />
          <p className="mb-4">
            We do not sell your personal data. We do not share it with third parties for their
            own marketing purposes. We work with a limited number of trusted sub-processors:
          </p>
          <div className="space-y-3 mb-4">
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Stripe, Inc. (USA) — Web Payment Processing <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Planned payment processor for the {APP_NAME} web application</p>
              <p className="text-sm text-muted-foreground">
                Stripe is not currently integrated. When paid plans launch, Stripe will process
                web subscription payments. Data shared will be limited to billing email and
                subscription events. Card details will be tokenised and handled entirely by Stripe;
                we will never store card data on our servers. Stripe is PCI-DSS Level 1 certified.
                Transfers to the US will be governed by Standard Contractual Clauses (SCCs).{" "}
                <a href="https://stripe.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Google LLC — Google Play Billing (USA) <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Planned in-app purchase processor for Android devices</p>
              <p className="text-sm text-muted-foreground">
                Google Play Billing is not currently active. When paid plans launch, Android
                in-app subscriptions will be processed by Google in accordance with Google Play's
                Developer Distribution Agreement and Payments Terms. We will receive only
                anonymised purchase confirmation and subscription status from Google.{" "}
                <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Apple Inc. — App Store In-App Purchase &amp; Apple Pay (USA) <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Planned in-app purchase processor for iOS / iPadOS devices</p>
              <p className="text-sm text-muted-foreground">
                Apple's payment systems are not currently active. When paid plans launch, iOS
                and iPadOS in-app subscriptions will be processed by Apple via their In-App
                Purchase system. Apple Pay may be offered where supported. We will receive only
                anonymised purchase receipts and subscription status from Apple.{" "}
                <a href="https://www.apple.com/legal/privacy/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Apple Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">RevenueCat, Inc. (USA) — Subscription Management <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Planned cross-platform subscription entitlement and management layer</p>
              <p className="text-sm text-muted-foreground">
                RevenueCat is not currently integrated. When paid plans launch, RevenueCat will
                manage subscription status, entitlements, and access control across iOS, Android,
                and the web using anonymised app user identifiers and purchase receipts. RevenueCat
                does <em>not</em> process payment card data. Transfers to the US will be governed
                by Standard Contractual Clauses (SCCs).{" "}
                <a href="https://www.revenuecat.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">RevenueCat Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Resend Inc. (USA)</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Transactional email delivery (account verification, reminders)</p>
              <p className="text-sm text-muted-foreground">Data shared: your email address and the content of transactional emails. Transfer basis: Standard Contractual Clauses (SCCs). <a href="https://resend.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Resend Privacy Policy →</a></p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Self-hosted File Storage (MinIO)</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Storing documents, photos, and files you upload</p>
              <p className="text-sm text-muted-foreground">Files are stored on servers we control. Data does not leave to a third-party cloud provider. Stored files are encrypted at rest and in transit.</p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Self-hosted Database (PostgreSQL)</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Primary data storage</p>
              <p className="text-sm text-muted-foreground">All account, property, tenant, financial, and lease data is stored on servers we control. Data is encrypted in transit (TLS) and backed up securely.</p>
            </div>
          </div>
          <p className="text-muted-foreground">
            We may also disclose personal data when required to do so by law, court order, or
            governmental authority, or when we believe in good faith that disclosure is necessary
            to protect our rights, protect your safety or the safety of others, or investigate fraud.
          </p>

          {/* ── Data Retention ── */}
          <SectionHeader id="data-retention" title="6. Data Retention" />
          <p className="mb-4">
            We retain your personal data only for as long as necessary to fulfil the purposes
            described in this policy, unless a longer retention period is required or permitted by law.
          </p>
          <div className="space-y-2 mb-4">
            <div className="flex gap-3 py-2 border-b border-border/40">
              <span className="font-medium text-foreground min-w-[180px] text-sm">Account data</span>
              <span className="text-sm text-muted-foreground">Retained for the duration of your account. Deleted within 30 days of account deletion request, except where retention is legally required.</span>
            </div>
            <div className="flex gap-3 py-2 border-b border-border/40">
              <span className="font-medium text-foreground min-w-[180px] text-sm">Property & tenant records</span>
              <span className="text-sm text-muted-foreground">Retained until you delete them or request account deletion.</span>
            </div>
            <div className="flex gap-3 py-2 border-b border-border/40">
              <span className="font-medium text-foreground min-w-[180px] text-sm">Financial & billing records</span>
              <span className="text-sm text-muted-foreground">Retained for 7 years as required by EU VAT and accounting regulations.</span>
            </div>
            <div className="flex gap-3 py-2 border-b border-border/40">
              <span className="font-medium text-foreground min-w-[180px] text-sm">Uploaded files & documents</span>
              <span className="text-sm text-muted-foreground">Deleted within 30 days of account deletion. You can delete individual files at any time.</span>
            </div>
            <div className="flex gap-3 py-2 border-b border-border/40">
              <span className="font-medium text-foreground min-w-[180px] text-sm">Technical/log data</span>
              <span className="text-sm text-muted-foreground">Retained for as long as necessary for security, performance, and debugging purposes.</span>
            </div>
            <div className="flex gap-3 py-2">
              <span className="font-medium text-foreground min-w-[180px] text-sm">Backups</span>
              <span className="text-sm text-muted-foreground">Deleted from backups within 90 days of the primary deletion.</span>
            </div>
          </div>

          {/* ── International Transfers ── */}
          <SectionHeader id="international-transfers" title="7. International Data Transfers" />
          <p className="mb-4">
            Our primary servers are located within the European Economic Area (EEA). Where we
            engage sub-processors based outside the EEA (currently Stripe, Google, Apple,
            RevenueCat, and Resend — all US-based), we ensure adequate safeguards are in place:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li><strong className="text-foreground">Standard Contractual Clauses (SCCs):</strong> We have entered into EU Standard Contractual Clauses (Commission Implementing Decision 2021/914) with all US-based sub-processors, as required by GDPR Chapter V.</li>
            <li><strong className="text-foreground">Data minimisation:</strong> We transfer only the minimum data necessary for the specific sub-processor to perform its service.</li>
            <li><strong className="text-foreground">Transfer Impact Assessments (TIAs):</strong> We conduct assessments for transfers to third countries to evaluate risks and additional safeguards.</li>
          </ul>
          <p className="text-muted-foreground">
            You may request a copy of the relevant SCCs by contacting us at{" "}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">{PRIVACY_EMAIL}</a>.
          </p>

          {/* ── Your Rights ── */}
          <SectionHeader id="your-rights" title="8. Your Rights Under GDPR" />
          <p className="mb-6">
            As a data subject under the GDPR, you have the following rights. To exercise any of
            these rights, contact us at{" "}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">{PRIVACY_EMAIL}</a>.
            We will respond without undue delay and within the timeframes required by applicable law.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <RightCard
              title="Right of Access (Art. 15)"
              description="You have the right to request a copy of the personal data we hold about you, along with information about how and why we use it."
            />
            <RightCard
              title="Right to Rectification (Art. 16)"
              description="You can ask us to correct inaccurate or incomplete personal data. You can update most profile information directly in your account settings."
            />
            <RightCard
              title="Right to Erasure / 'Right to be Forgotten' (Art. 17)"
              description="You can request deletion of your personal data where it is no longer necessary for the purpose it was collected, or where you withdraw consent. Some data may be retained for legal obligations."
            />
            <RightCard
              title="Right to Restriction of Processing (Art. 18)"
              description="You can request that we restrict processing of your data while a dispute about its accuracy or the lawfulness of processing is resolved."
            />
            <RightCard
              title="Right to Data Portability (Art. 20)"
              description="Where processing is based on contract or consent, you can request your personal data in a structured, commonly used, machine-readable format (JSON/CSV export)."
            />
            <RightCard
              title="Right to Object (Art. 21)"
              description="You can object to processing based on legitimate interests. We will cease processing unless we can demonstrate compelling legitimate grounds that override your interests."
            />
            <RightCard
              title="Rights Related to Automated Decision-Making (Art. 22)"
              description="We do not use fully automated decision-making (including profiling) that produces significant legal effects. If this changes, we will update this policy and seek consent."
            />
            <RightCard
              title="Right to Withdraw Consent (Art. 7(3))"
              description="Where processing is based on your consent, you may withdraw it at any time without penalty. Withdrawal does not affect the lawfulness of processing before withdrawal."
            />
          </div>
          <InfoBox>
            To exercise any of these rights or to request complete account and data deletion,
            see <a href="#delete-your-data" className="text-primary hover:underline font-medium">Section 9 — Delete Your Data</a> for
            step-by-step instructions.
          </InfoBox>

          {/* ── Delete Your Data ── */}
          <SectionHeader id="delete-your-data" title="9. Delete Your Data" />
          <p className="mb-6 text-muted-foreground">
            You have the right to request the permanent deletion of your account and all
            associated personal data at any time. This is free of charge and will be processed
            without undue delay, and in any event within one month, as required by law.
          </p>

          {/* Action card */}
          <div className="bg-destructive/5 border border-destructive/20 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 bg-destructive/10 rounded-xl">
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="font-bold text-foreground">Request Account Deletion</p>
                <p className="text-xs text-muted-foreground">Permanently removes your account and all data</p>
              </div>
            </div>

            <ol className="space-y-4 mb-6">
              <li className="flex gap-4">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-destructive/10 text-destructive font-bold text-xs shrink-0 mt-0.5">1</div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-sm text-foreground">Send an email to</span>
                  </div>
                  <a
                    href={`mailto:${PRIVACY_EMAIL}?subject=Data%20Deletion%20Request&body=Hello%2C%0A%0AI%20would%20like%20to%20request%20permanent%20deletion%20of%20my%20account%20and%20all%20associated%20personal%20data.%0A%0AAccount%20email%3A%20%5Byour-account-email%5D%0A%0AThank%20you.`}
                    className="inline-flex items-center gap-1.5 text-sm font-mono bg-background border border-border/60 rounded-lg px-3 py-1.5 text-primary hover:bg-muted/50 transition-colors"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {PRIVACY_EMAIL}
                  </a>
                </div>
              </li>

              <li className="flex gap-4">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-destructive/10 text-destructive font-bold text-xs shrink-0 mt-0.5">2</div>
                <div>
                  <p className="font-semibold text-sm text-foreground mb-1">Use this subject line</p>
                  <code className="text-sm bg-background border border-border/60 rounded-lg px-3 py-1.5 font-mono block w-fit">
                    Data Deletion Request
                  </code>
                </div>
              </li>

              <li className="flex gap-4">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-destructive/10 text-destructive font-bold text-xs shrink-0 mt-0.5">3</div>
                <div>
                  <p className="font-semibold text-sm text-foreground mb-1">Include in your email</p>
                  <p className="text-sm text-muted-foreground">
                    The email address registered to your {APP_NAME} account, so we can identify
                    and verify your request.
                  </p>
                </div>
              </li>
            </ol>

            <div className="grid sm:grid-cols-3 gap-3 pt-4 border-t border-destructive/10">
              <div className="flex items-start gap-2.5">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Response time</p>
                  <p className="text-xs text-muted-foreground">Without undue delay</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Confirmation</p>
                  <p className="text-xs text-muted-foreground">We email you when complete</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Shield className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Cost</p>
                  <p className="text-xs text-muted-foreground">Always free of charge</p>
                </div>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-foreground mb-2">What gets deleted</h3>
          <div className="grid sm:grid-cols-2 gap-2 mb-4">
            {[
              "Your account profile and login credentials",
              "All property and room data",
              "All tenant records and contact information",
              "All lease agreements and rent records",
              "All uploaded documents, photos, and files",
              "Maintenance requests and notes",
              "Notification preferences and settings",
              "Authentication tokens and sessions",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <h3 className="font-semibold text-foreground mb-2 mt-5">What may be retained</h3>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200 mb-4">
            <p className="font-semibold mb-1">Financial & billing records — retained up to 7 years</p>
            <p className="leading-relaxed">
              EU VAT and accounting regulations (Council Directive 2006/112/EC and national tax
              laws) require us to retain billing records and invoices for up to 7 years from the
              date of the transaction, even after account deletion. This data is strictly
              isolated, used only for tax compliance, and never used for any other purpose.
              It will be automatically purged once the legal retention period expires.
            </p>
          </div>

          {/* ── Cookies ── */}
          <SectionHeader id="cookies" title="10. Cookies & Tracking" />
          <p className="mb-4">
            We use only the cookies strictly necessary to operate the Service. We do not use
            advertising cookies, third-party tracking cookies, or profiling technologies.
          </p>
          <div className="space-y-2 mb-4">
            <div className="flex gap-3 py-2.5 border-b border-border/40">
              <div className="min-w-[160px]">
                <p className="text-sm font-medium text-foreground">Auth Session Token</p>
                <p className="text-xs text-muted-foreground">Essential (localStorage)</p>
              </div>
              <p className="text-sm text-muted-foreground">Keeps you logged in. Expires after inactivity or on sign-out. Required for the Service to function.</p>
            </div>
            <div className="flex gap-3 py-2.5 border-b border-border/40">
              <div className="min-w-[160px]">
                <p className="text-sm font-medium text-foreground">UI Preferences</p>
                <p className="text-xs text-muted-foreground">Essential (localStorage)</p>
              </div>
              <p className="text-sm text-muted-foreground">Stores your UI preferences (e.g. dismissed banners) locally on your device. Not transmitted to our servers.</p>
            </div>
          </div>
          <p className="text-muted-foreground">
            We do not use Google Analytics, Facebook Pixel, or any other third-party tracking
            services. If this changes, we will update this policy and, where required, obtain
            your prior consent through a cookie consent mechanism.
          </p>
          <p className="mt-3 text-muted-foreground">
            You can clear locally stored data at any time through your browser or device settings.
            Clearing authentication tokens will sign you out of the Service.
          </p>

          {/* ── Security ── */}
          <SectionHeader id="security" title="11. Security" />
          <p className="mb-4">
            We implement appropriate technical and organisational measures to protect your personal
            data against accidental or unlawful destruction, loss, alteration, unauthorised
            disclosure, or access (as required by GDPR Article 32). These include:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>All data transmitted between your browser and our servers is encrypted using TLS 1.2 or higher (HTTPS).</li>
            <li>Passwords are hashed using bcrypt with a cost factor that exceeds current security recommendations. Plaintext passwords are never stored.</li>
            <li>Database access is restricted to authenticated backend services only; no direct public database access is possible.</li>
            <li>File storage is secured with pre-signed URL access controls; files are not publicly accessible without authorisation.</li>
            <li>Regular security reviews and dependency updates are conducted to address known vulnerabilities.</li>
            <li>Access to production systems is restricted to authorised personnel via role-based access controls.</li>
            <li>Regular encrypted backups are maintained with restricted access.</li>
          </ul>
          <p className="text-muted-foreground">
            In the event of a personal data breach that poses a risk to your rights and freedoms,
            we will notify the relevant supervisory authority within 72 hours of becoming aware
            of the breach (as required by GDPR Article 33) and, where required, notify affected
            individuals without undue delay.
          </p>

          {/* ── Children ── */}
          <SectionHeader id="children" title="12. Children's Privacy" />
          <p className="mb-3">
            The Service is intended for use by adults (aged 18 and over) in a professional
            landlord capacity. We do not knowingly collect personal data from children under the
            age of 16.
          </p>
          <p className="text-muted-foreground">
            If you believe we have inadvertently collected data from a minor, please contact us
            immediately at{" "}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">{PRIVACY_EMAIL}</a>{" "}
            and we will take immediate steps to delete such data.
          </p>

          {/* ── Changes ── */}
          <SectionHeader id="changes" title="13. Changes to This Policy" />
          <p className="mb-3">
            We may update this Privacy Policy from time to time to reflect changes in our
            practices, technology, legal requirements, or other factors.
          </p>
          <p className="mb-3 text-muted-foreground">
            When we make material changes, we will:
          </p>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li>Update the "Last updated" date at the top of this page.</li>
            <li>Send an email notification to registered users for significant changes that affect their rights.</li>
            <li>Where required by law, obtain fresh consent before processing data under the new terms.</li>
          </ul>
          <p className="text-muted-foreground">
            We encourage you to review this policy periodically. Continued use of the Service
            after the effective date of changes constitutes acceptance of the revised policy for
            non-material changes.
          </p>

          {/* ── Contact ── */}
          <SectionHeader id="contact" title="14. Contact & How to Complain" />
          <p className="mb-4">
            For any questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us:
          </p>
          <div className="bg-muted/40 rounded-xl p-4 border border-border/40 text-sm mb-6 space-y-1">
            <p><strong>Privacy enquiries:</strong>{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">{PRIVACY_EMAIL}</a>
            </p>
            <p><strong>Data deletion requests:</strong>{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">{PRIVACY_EMAIL}</a>{" "}
              (subject: "Data Deletion Request")
            </p>
            <p><strong>Legal / DPO contact:</strong>{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-primary hover:underline">{LEGAL_EMAIL}</a>
            </p>
          </div>
          <p className="mb-4 text-muted-foreground">
            If you are not satisfied with our response, you have the right to lodge a complaint
            with your national data protection supervisory authority (your local {SUPERVISORY_AUTHORITY}).
            In the EU, you can find your national DPA at:{" "}
            <a
              href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              European Data Protection Board — Members
            </a>.
          </p>
          <p className="text-muted-foreground">
            You also have the right to seek an effective judicial remedy against a data controller
            or processor (GDPR Article 79).
          </p>

          {/* Bottom cross-links */}
          <div className="mt-14 pt-8 border-t border-border/60 flex flex-col sm:flex-row gap-4">
            <Link
              to="/terms-of-service"
              className="flex-1 bg-muted/40 hover:bg-muted/70 border border-border/40 rounded-xl p-4 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Also read</p>
              <p className="font-semibold text-foreground text-sm">Terms of Service →</p>
            </Link>
            <Link
              to="/"
              className="flex-1 bg-muted/40 hover:bg-muted/70 border border-border/40 rounded-xl p-4 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Back to</p>
              <p className="font-semibold text-foreground text-sm">{APP_NAME} Home →</p>
            </Link>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-muted/20 mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors font-medium text-primary">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
